import { DEFAULT_BOT_PARAMS, type BotParams } from '../controllers/BotController.ts';
import { DEFAULT_EMG_PARAMS, type EmgParams } from '../controllers/EmgController.ts';
import {
  DEFAULT_STIM_CONTROLLER_OPTIONS,
  type StimulationControllerOptions,
} from '../controllers/StimulationController.ts';
import { DEFAULT_CONFIG, type GameConfig } from '../game/GameState.ts';
import type { RenderTheme } from '../game/Renderer.ts';
import { DEFAULT_SIMULATED_EMG, type SimulatedEmgOptions } from '../hardware/SimulatedEmgDevice.ts';
import {
  DEFAULT_HHI_OPTIONS,
  type HHIAdapterOptions,
} from '../stimulation/BackyardBrainsHHIAdapter.ts';
import { DEFAULT_SAFETY_LIMITS, type SafetyLimits } from '../stimulation/SafetyGate.ts';
import {
  DEFAULT_HUMAN_MODEL,
  type HumanResponseModel,
} from '../stimulation/SimulatedHumanStimulator.ts';
import type { ModeId } from './Modes.ts';

/** Which physical input device a hardware mode should use. */
export type InputDeviceId = 'keyboard-key' | 'serial' | 'websocket' | 'simulated-emg';

export interface AppConfig {
  mode: ModeId;
  game: GameConfig;
  bot: BotParams;
  human: HumanResponseModel;
  safety: SafetyLimits;
  stimController: StimulationControllerOptions;
  emg: EmgParams;
  simulatedEmg: SimulatedEmgOptions;
  hhi: HHIAdapterOptions;

  /** Device used by the Physical Button and HHI modes. */
  buttonDevice: InputDeviceId;
  /** Device used by the EMG mode. */
  emgDevice: InputDeviceId;
  /** Key that stands in for an external switch during development. */
  keyboardButtonKey: string;
  websocketUrl: string;

  /**
   * Apply the operator safety limits even when the stimulator is simulated.
   *
   * Off by default: in simulation the limits throttle a synthetic actuator and
   * mask the response model being studied. Turn it on to measure the effect of
   * the rate limiter itself, which is what a real participant will experience.
   */
  enforceSafetyLimitsInSimulation: boolean;

  /** Visual style. See `RenderTheme`. */
  renderTheme: RenderTheme;

  showBotVision: boolean;
  /** Attach a full state snapshot to every logged event. */
  logGameState: boolean;
}

export function defaultConfig(): AppConfig {
  return {
    mode: 'keyboard',
    game: { ...DEFAULT_CONFIG },
    bot: { ...DEFAULT_BOT_PARAMS },
    human: { ...DEFAULT_HUMAN_MODEL },
    safety: { ...DEFAULT_SAFETY_LIMITS },
    stimController: { ...DEFAULT_STIM_CONTROLLER_OPTIONS },
    emg: { ...DEFAULT_EMG_PARAMS },
    simulatedEmg: { ...DEFAULT_SIMULATED_EMG },
    hhi: { ...DEFAULT_HHI_OPTIONS },
    buttonDevice: 'keyboard-key',
    emgDevice: 'simulated-emg',
    keyboardButtonKey: 'Space',
    websocketUrl: 'ws://localhost:8765',
    enforceSafetyLimitsInSimulation: false,
    renderTheme: 'arcade',
    showBotVision: true,
    logGameState: true,
  };
}

const STORAGE_KEY = 'flappy-hhi/config/v1';

/**
 * Persist configuration between sessions.
 *
 * Two things are deliberately never restored:
 *
 *   - the mode, which always starts at `keyboard`
 *   - `hhi.operatorAcknowledgedUnverified`, which always starts false
 *
 * A stimulation setup must be re-established by a person at the start of every
 * session. Reloading a page is not consent, and a flag saved last Tuesday is
 * not a safety check.
 */
export function saveConfig(config: AppConfig): void {
  try {
    const copy: AppConfig = {
      ...config,
      hhi: { ...config.hhi, operatorAcknowledgedUnverified: false },
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(copy));
  } catch {
    // Private browsing, disabled storage: not worth interrupting a session for.
  }
}

export function loadConfig(): AppConfig {
  const base = defaultConfig();
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return base;
    const saved = JSON.parse(raw) as Partial<AppConfig>;
    return {
      ...base,
      ...saved,
      mode: 'keyboard',
      // Space is the only gameplay input. Do not revive the former KeyB
      // development binding from an older saved configuration.
      keyboardButtonKey: 'Space',
      game: { ...base.game, ...saved.game },
      bot: { ...base.bot, ...saved.bot },
      human: { ...base.human, ...saved.human },
      safety: { ...base.safety, ...saved.safety },
      stimController: { ...base.stimController, ...saved.stimController },
      emg: { ...base.emg, ...saved.emg },
      simulatedEmg: { ...base.simulatedEmg, ...saved.simulatedEmg },
      hhi: { ...base.hhi, ...saved.hhi, operatorAcknowledgedUnverified: false },
    };
  } catch {
    return base;
  }
}

export function clearSavedConfig(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}
